import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import BookingRequest, {
  CalendarSlot,
  RequestStatus,
} from "src/app/models/screens/requestManagement.model";
import {
  acceptBookingRequest,
  decideOnDetachFromParents,
  getAcceptedRequestsForDate,
  getDetachRequests,
  getLogs,
  getParentChildren,
  getReceivedBookingRequests,
  rejectBookingRequest,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

interface SelectorUser {
  id: string;
  label: string;
}

interface DetachRequestParent {
  fullName: string;
}

interface DetachRequestModel {
  id: string;
  birthdate: string;
  fullName: string;
}

export interface DetachRequestItem {
  model: DetachRequestModel;
  parents: DetachRequestParent[];
}

export interface DashboardLogEntry {
  id: string;
  type: string;
  createdAt: string;
  text: string;
}

const DashboardScreenViewModel = () => {
  const [role, setRole] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isFirstTimeVisiting, setIsFirstTimeVisiting] =
    useState<boolean>(false);

  const [selectorUsers, setSelectorUsers] = useState<SelectorUser[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedDashboardOwnerId, setSelectedDashboardOwnerId] =
    useState<string>("you");

  const [receivedRequests, setReceivedRequests] = useState<BookingRequest[]>(
    [],
  );
  const [isLoadingReceivedRequests, setIsLoadingReceivedRequests] =
    useState<boolean>(false);

  const [todayEvents, setTodayEvents] = useState<CalendarSlot[]>([]);
  const [isLoadingTodayEvents, setIsLoadingTodayEvents] =
    useState<boolean>(false);

  const [logs, setLogs] = useState<DashboardLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false);

  const [detachRequests, setDetachRequests] = useState<DetachRequestItem[]>([]);
  const [isLoadingDetachRequests, setIsLoadingDetachRequests] =
    useState<boolean>(false);
  const [processingDetachRequestId, setProcessingDetachRequestId] = useState<
    string | null
  >(null);

  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [now, setNow] = useState<Dayjs>(dayjs());

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const getCurrentChildId = useCallback((): string | undefined => {
    if (role !== "Parent") return undefined;
    return selectedDashboardOwnerId === "you"
      ? undefined
      : selectedDashboardOwnerId;
  }, [role, selectedDashboardOwnerId]);

  const getLogsUserId = useCallback((): string | undefined => {
    if (role === "Parent") {
      return selectedDashboardOwnerId === "you"
        ? currentUserId || undefined
        : selectedDashboardOwnerId;
    }

    return currentUserId || undefined;
  }, [currentUserId, role, selectedDashboardOwnerId]);

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
    setIsFirstTimeVisiting(
      localStorage.getItem(localStorageConstants.FIRST_TIME_VISITING) ===
        "true",
    );
    localStorage.setItem(localStorageConstants.FIRST_TIME_VISITING, "false");

    const storedUserId =
      localStorage.getItem((localStorageConstants as any).ID) ??
      localStorage.getItem("ID") ??
      "";
    setCurrentUserId(storedUserId);
  }, []);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const fetchedChildren: ParentChild[] = response.children ?? [];
    setSelectorUsers(
      fetchedChildren.map((child: ParentChild) => ({
        id: child._id,
        label: child.firstName,
      })),
    );
  }, []);

  const loadReceivedRequests = useCallback(async (childId?: string) => {
    setIsLoadingReceivedRequests(true);
    const response = await getReceivedBookingRequests(childId);
    setIsLoadingReceivedRequests(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setReceivedRequests(
      (response.requests ?? []).filter(
        (request: BookingRequest) => request.status === "Pending",
      ),
    );
  }, []);

  const loadTodayEvents = useCallback(
    async (date: string, childId?: string) => {
      setIsLoadingTodayEvents(true);
      const response = await getAcceptedRequestsForDate(date, childId);
      setIsLoadingTodayEvents(false);

      if (!response.success) {
        showError(response.error);
        return;
      }

      setTodayEvents(response.requests ?? []);
    },
    [],
  );

  const loadLogs = useCallback(async (userId?: string) => {
    setIsLoadingLogs(true);
    const response = await getLogs(1, "warpa", userId);
    setIsLoadingLogs(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setLogs((response.logs ?? []).slice(0, 10));
  }, []);

  const loadDetachRequests = useCallback(async () => {
    setIsLoadingDetachRequests(true);
    const response = await getDetachRequests();
    setIsLoadingDetachRequests(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setDetachRequests(response.requests ?? []);
  }, []);

  useEffect(() => {
    if (role !== "Parent") {
      setSelectorUsers([]);
      return;
    }

    loadParentChildren();
  }, [role, loadParentChildren]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!role) return;
    if (role !== "Guest") loadReceivedRequests(getCurrentChildId());
    loadTodayEvents(now.format("YYYY-MM-DD"), getCurrentChildId());
    loadLogs(getLogsUserId());

    if (role === "Admin") {
      loadDetachRequests();
    }
  }, [
    role,
    selectedDashboardOwnerId,
    getCurrentChildId,
    getLogsUserId,
    loadDetachRequests,
    loadLogs,
    loadReceivedRequests,
    loadTodayEvents,
    now,
  ]);

  useEffect(() => {
    const currentDate = dayjs().format("YYYY-MM-DD");
    if (now.format("YYYY-MM-DD") !== currentDate) return;
    if (!role) return;

    loadTodayEvents(now.format("YYYY-MM-DD"), getCurrentChildId());
  }, [now, getCurrentChildId, loadTodayEvents, role]);

  useEffect(() => {
    const compareRequests = (a: BookingRequest, b: BookingRequest) => {
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (a.status !== "Pending" && b.status === "Pending") return 1;

      if (a.status === "Pending" && b.status === "Pending") {
        if (a.emergency && !b.emergency) return -1;
        if (!a.emergency && b.emergency) return 1;
      }

      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;

      return a.from - b.from;
    };

    const isAlreadySorted = receivedRequests.every((request, index, arr) => {
      if (index === 0) return true;
      return compareRequests(arr[index - 1], request) <= 0;
    });

    if (!isAlreadySorted) {
      setReceivedRequests((prev) => [...prev].sort(compareRequests));
    }
  }, [receivedRequests]);

  useEffect(() => {
    const isAlreadySorted = todayEvents.every((event, index, arr) => {
      if (index === 0) return true;
      return arr[index - 1].from <= event.from;
    });

    if (!isAlreadySorted) {
      setTodayEvents((prev) => [...prev].sort((a, b) => a.from - b.from));
    }
  }, [todayEvents]);

  const onSelectedDashboardOwnerChanged = (ownerId: string) => {
    setSelectedDashboardOwnerId(ownerId);
  };

  const onAcceptRequest = async (requestId: string) => {
    const response = await acceptBookingRequest(requestId, getCurrentChildId());

    if (!response.success) {
      showError(response.error);
      return;
    }

    setReceivedRequests((prev) =>
      prev.map((request) =>
        response.updatedRequests.some(
          (rawRequest: any) => rawRequest.id === request.id,
        )
          ? {
              ...request,
              status: request.id === requestId ? "Accepted" : "Rejected",
            }
          : request,
      ),
    );

    await loadTodayEvents(now.format("YYYY-MM-DD"), getCurrentChildId());
    await loadLogs(getLogsUserId());
    showSuccess("Request accepted successfully.");
  };

  const onRejectRequest = async (requestId: string) => {
    const response = await rejectBookingRequest(requestId, getCurrentChildId());

    if (!response.success) {
      showError(response.error);
      return;
    }

    if (response.updatedRequest) {
      setReceivedRequests((prev) =>
        prev.map((request) =>
          request.id === response.updatedRequest.id
            ? { ...request, status: "Rejected" }
            : request,
        ),
      );
    } else {
      setReceivedRequests((prev) =>
        prev.map((request) =>
          request.id === requestId
            ? { ...request, status: "Rejected" }
            : request,
        ),
      );
    }

    await loadLogs(getLogsUserId());
    showSuccess("Request rejected successfully.");
  };

  const onDetachDecision = async (
    request: DetachRequestItem,
    detach: boolean,
  ) => {
    setProcessingDetachRequestId(request.model.id);

    const response = await decideOnDetachFromParents(detach, request.model.id);

    setProcessingDetachRequestId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setDetachRequests((prev) =>
      prev.filter((item) => item.model.id !== request.model.id),
    );
    await loadLogs(getLogsUserId());
    showSuccess(detach ? "User detached successfully." : "Request dismissed.");
  };

  const onDetachPressed = async (request: DetachRequestItem) => {
    await onDetachDecision(request, true);
  };

  const onDismissPressed = async (request: DetachRequestItem) => {
    await onDetachDecision(request, false);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours: number = Math.floor(minutes / 60);
    const mins: number = minutes % 60;

    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const getStatusChipColor = (
    status: RequestStatus,
  ): "warning" | "success" | "error" => {
    if (status === "Accepted") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };

  const getLogTypeChipColor = (
    type: string,
  ): "default" | "primary" | "secondary" | "success" | "warning" | "error" => {
    const normalizedType = type.trim().toLowerCase();

    if (
      normalizedType.includes("error") ||
      normalizedType.includes("fail") ||
      normalizedType.includes("reject")
    )
      return "error";

    if (
      normalizedType.includes("warning") ||
      normalizedType.includes("suspend")
    )
      return "warning";

    if (
      normalizedType.includes("success") ||
      normalizedType.includes("accept") ||
      normalizedType.includes("created")
    )
      return "success";

    if (normalizedType.includes("update") || normalizedType.includes("edit"))
      return "primary";

    if (normalizedType.includes("link") || normalizedType.includes("relation"))
      return "secondary";

    return "default";
  };

  const formatDateTime = (value: string): string => {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleString();
  };

  const highlightedEventId = useMemo(() => {
    if (todayEvents.length === 0) return null;

    const currentMinutes = now.hour() * 60 + now.minute();

    const collidingEvent = todayEvents.find((event) => {
      if (event.emergency) return false;
      return currentMinutes >= event.from && currentMinutes < event.to;
    });

    if (collidingEvent) return collidingEvent.id;

    const nextEvent = todayEvents.find((event) => event.from >= currentMinutes);
    if (nextEvent) return nextEvent.id;

    return null;
  }, [todayEvents, now]);

  return {
    role,
    selectorUsers,
    isLoadingChildren,
    selectedDashboardOwnerId,
    onSelectedDashboardOwnerChanged,

    receivedRequests,
    isLoadingReceivedRequests,
    onAcceptRequest,
    onRejectRequest,

    todayEvents,
    isLoadingTodayEvents,
    highlightedEventId,

    logs,
    isLoadingLogs,

    detachRequests,
    isLoadingDetachRequests,
    processingDetachRequestId,
    onDetachPressed,
    onDismissPressed,

    minutesToLabel,
    getStatusChipColor,
    getLogTypeChipColor,
    formatDateTime,

    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    isFirstTimeVisiting,
    setIsFirstTimeVisiting,
  };
};

export default DashboardScreenViewModel;
